/**
 * NACA (Neighborhood Assistance Corporation of America) partner content.
 *
 * Single source of truth for the reusable referral card and the dedicated
 * /partners/naca explainer page. NACA is an independent nonprofit — we are not
 * paid for these referrals and we do not control their program terms.
 */

const SITE = "https://www.homeapproach.com";

export const NACA_PATH = "/partners/naca";
export const NACA_URL = `${SITE}${NACA_PATH}`;
export const NACA_EXTERNAL_URL = "https://www.naca.com/";
export const NACA_WORKSHOP_URL = "https://www.naca.com/homebuyer-workshops/";

export const NACA_TITLE =
  "NACA Home Loan: No Down Payment, No PMI — Is It Worth It? (2026)";
export const NACA_DESCRIPTION =
  "NACA's mortgage has no down payment, no closing costs, no PMI, and no credit-score minimum — in exchange for a long, counseling-heavy process. Who it fits, who it doesn't, and how it compares to stacking grants and DPA.";

/** One-line pitch used on the referral card. */
export const NACA_CARD_HEADLINE = "A different path: no down payment, no PMI";
export const NACA_CARD_BODY =
  "NACA is a nonprofit with its own mortgage program — no down payment, no closing costs, no PMI, and no credit-score minimum. The trade is a long, counseling-heavy process with strict rules. It's worth knowing about before you assume a grant stack is your only route.";

export const NACA_CARD_POINTS: string[] = [
  "No down payment and no PMI",
  "No credit score minimum",
  "Owner-occupancy required",
  "Months, not weeks",
];

export const NACA_DISCLOSURE =
  "NACA is an independent nonprofit. Home Approach is not affiliated with NACA, receives nothing for this referral, and cannot influence whether you're accepted or what terms you're offered.";

export const NACA_FITS: string[] = [
  "You have little or no savings for a down payment or closing costs.",
  "Your credit is thin or bruised and traditional pre-approval keeps stalling.",
  "You plan to live in the home yourself and don't own other property.",
  "You can commit to counseling sessions, document requests, and a timeline measured in months.",
];

export const NACA_DOES_NOT_FIT: string[] = [
  "You need to close in the next 60 to 90 days.",
  "You're buying an investment property or a second home.",
  "You already own another home and plan to keep it.",
  "You want to shop several lenders against each other — NACA's mortgage runs only through its own process.",
];

export type NacaStep = { title: string; detail: string };

export const NACA_STEPS: NacaStep[] = [
  {
    title: "Attend a homebuyer workshop",
    detail:
      "Free and open to anyone. This is the entry point — you cannot skip it. Workshops run online and in person.",
  },
  {
    title: "Meet with a housing counselor",
    detail:
      "You're assigned a counselor who reviews income, debts, payment history, and savings habits. Expect several sessions.",
  },
  {
    title: "Reach NACA Qualification",
    detail:
      "The counselor certifies you as ready. This is the step that takes the longest and is where most of the paperwork lives.",
  },
  {
    title: "Get a Qualification letter and shop",
    detail:
      "You search within your approved payment, with NACA's agent network or your own, and the home must meet their condition standards.",
  },
  {
    title: "Close through NACA's lender",
    detail:
      "The mortgage is funded through NACA's participating lenders on their terms. You can buy down the rate with an optional interest-rate buy-down.",
  },
];

export type NacaCompareRow = { factor: string; naca: string; stack: string };

export const NACA_COMPARE: NacaCompareRow[] = [
  { factor: "Down payment", naca: "None required", stack: "Reduced or covered by grants and DPA" },
  { factor: "Mortgage insurance", naca: "None", stack: "Usually required under 20% down" },
  { factor: "Credit score minimum", naca: "None — payment history reviewed instead", stack: "Typically 580 to 640+" },
  { factor: "Timeline", naca: "Often 6 to 18 months", stack: "Weeks once you're pre-approved" },
  { factor: "Lender choice", naca: "NACA's participating lenders only", stack: "Any approved lender" },
  { factor: "Property use", naca: "Owner-occupied only, no other property", stack: "Varies by program" },
  { factor: "Cost to participate", naca: "No fees; membership dues apply", stack: "Free to search and apply" },
];

export type NacaFaq = { q: string; a: string };

export const NACA_FAQS: NacaFaq[] = [
  {
    q: "Is NACA a grant?",
    a: "No. NACA is a nonprofit that runs a counseling program and its own mortgage product. There is no down payment and no closing costs, but you are taking out a mortgage, not receiving free money.",
  },
  {
    q: "Is there really no credit score minimum?",
    a: "NACA does not use a credit score cutoff. Instead, counselors review your actual payment history — rent, utilities, and other recurring obligations — over recent months. Recent missed payments still matter.",
  },
  {
    q: "How long does NACA take?",
    a: "Most buyers should plan for six to eighteen months from the first workshop to closing, depending on how quickly they can document income and clear up payment history. It is not a fast path.",
  },
  {
    q: "Can I combine NACA with down payment assistance?",
    a: "Generally no in the usual sense — NACA already requires no down payment, and its mortgage runs through its own process, which limits layering outside DPA on top. If you want to stack grants and employer help, that is a different route.",
  },
  {
    q: "Does Home Approach get paid for NACA referrals?",
    a: "No. NACA is an independent nonprofit and we receive nothing for pointing you there. We list it because for some buyers it is genuinely the better path.",
  },
];

export const NACA_JSON_LD: Record<string, unknown> = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "WebPage",
      name: NACA_TITLE,
      description: NACA_DESCRIPTION,
      url: NACA_URL,
      inLanguage: "en-US",
      isPartOf: { "@type": "WebSite", name: "Home Approach", url: SITE },
      publisher: {
        "@type": "Organization",
        name: "Home Approach",
        url: SITE,
        logo: { "@type": "ImageObject", url: `${SITE}/og-image.png` },
      },
    },
    {
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/` },
        { "@type": "ListItem", position: 2, name: "Partners", item: `${SITE}/partners/naca` },
        { "@type": "ListItem", position: 3, name: NACA_TITLE, item: NACA_URL },
      ],
    },
    {
      "@type": "FAQPage",
      mainEntity: NACA_FAQS.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    },
  ],
};
